import React from 'react';
import Icon from './AppIcon';

const VendorBadge = ({ vendor, badgeType, size = 'sm' }) => {
  const calculateBadgeEligibility = (vendor, badgeType) => {
    const {
      rating = 0,
      completedJobs = 0,
      complaintRate = 0,
      platformActivityDays = 0,
      medianResponseTime = 24,
      leadResponseRate = 0,
      maxResponseGap = 48,
      completedMiBuyProjects = 0,
      hasIdentityVerification = false,
      hasBusinessLicense = false,
      noRecentDisputes = true,
      quoteAcceptanceRate = 0,
      usesToolsConsistently = false,
      quotesDelivered = 0,
      cancellationRate = 0,
      profileCompletion = 0,
      quotesPerMonth = 0,
      noTosViolations = true,
      engagementScore = 0,
      hasGovernmentId = false,
      hasVerifiedPhone = false,
      hasVerifiedEmail = false,
      hasValidatedEin = false,
      hasConfirmedAddress = false,
      hasBusinessLicense: hasLicense = false,
      hasInsuranceCertificate = false,
      manuallyValidated = false,
      hasManufacturerCertification = false,
      isPaidFeatured = false,
      onTimeRate = 0,
      favoritesSaveRate = 0,
      quoteAccuracyRate = 0
    } = vendor;

    switch (badgeType) {
      case 'mibuy-top-rated':
        return rating >= 4.5 && completedJobs >= 10 && complaintRate < 5 && platformActivityDays >= 30;
      
      case 'good-communication':
        return medianResponseTime < 4 && leadResponseRate >= 90 && maxResponseGap <= 24;
      
      case 'qualified-pro':
        return rating >= 4.0 && completedMiBuyProjects >= 5 && hasIdentityVerification && hasBusinessLicense && noRecentDisputes;
      
      case 'mibuy-preferred-vendor':
        return quoteAcceptanceRate >= 50 && usesToolsConsistently && quotesDelivered >= 10 && cancellationRate < 3;
      
      case 'consistent-performer':
        return profileCompletion >= 90 && quotesPerMonth >= 10 && noTosViolations && engagementScore >= 80;
      
      case 'identity-verified':
        return hasGovernmentId && hasVerifiedPhone && hasVerifiedEmail && hasValidatedEin && hasConfirmedAddress;
      
      case 'licensed-insured':
        return hasLicense && hasInsuranceCertificate && manuallyValidated;
      
      case 'manufacturer-certified':
        return hasManufacturerCertification;
      
      case 'featured-vendor':
        return isPaidFeatured;
      
      case 'on-time-pro':
        return onTimeRate >= 95;
      
      case 'customer-favorite':
        return favoritesSaveRate >= 75;
      
      case 'quote-accuracy':
        return quoteAccuracyRate >= 90;
      
      default:
        return false;
    }
  };

  const getBadgeConfig = (badgeType) => {
    const configs = {
      'mibuy-top-rated': {
        icon: 'Crown',
        label: 'Stackd Top Rated',
        emoji: '⭐',
        color: 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white',
        borderColor: 'border-yellow-400',
        description: '4.5+ stars, 10+ jobs, <5% complaints, 30+ days active'
      },
      'good-communication': {
        icon: 'MessageCircle',
        label: 'Good Communication',
        emoji: '⚡',
        color: 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white',
        borderColor: 'border-blue-400',
        description: '<4hr response, 90%+ lead response, <24hr gaps'
      },
      'qualified-pro': {
        icon: 'ShieldCheck',
        label: 'Qualified Pro',
        emoji: '🛠',
        color: 'bg-gradient-to-r from-green-500 to-emerald-500 text-white',
        borderColor: 'border-green-400',
        description: '4.0+ rating, 5+ Stackd projects, verified & licensed'
      },
      'mibuy-preferred-vendor': {
        icon: 'Award',
        label: 'Stackd Preferred',
        emoji: '💼',
        color: 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white',
        borderColor: 'border-purple-400',
        description: '50%+ quote acceptance, uses Stackd tools, 10+ quotes'
      },
      'consistent-performer': {
        icon: 'TrendingUp',
        label: 'Consistent Performer',
        emoji: '📈',
        color: 'bg-gradient-to-r from-orange-500 to-red-500 text-white',
        borderColor: 'border-orange-400',
        description: '90%+ profile complete, 10+ quotes/month, high engagement'
      },
      'identity-verified': {
        icon: 'UserCheck',
        label: 'Identity Verified',
        emoji: '🪪',
        color: 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white',
        borderColor: 'border-teal-400',
        description: 'Government ID, phone, email, EIN, address verified'
      },
      'licensed-insured': {
        icon: 'Shield',
        label: 'Licensed & Insured',
        emoji: '🏢',
        color: 'bg-gradient-to-r from-gray-600 to-gray-700 text-white',
        borderColor: 'border-gray-500',
        description: 'Business license & insurance verified'
      },
      'manufacturer-certified': {
        icon: 'Certificate',
        label: 'Manufacturer Certified',
        emoji: '🧰',
        color: 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white',
        borderColor: 'border-amber-400',
        description: 'Approved reseller/service partner of major brands'
      },
      'featured-vendor': {
        icon: 'Star',
        label: 'Featured Vendor',
        emoji: '🔶',
        color: 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold',
        borderColor: 'border-yellow-500',
        description: 'Premium listing with enhanced visibility'
      },
      'on-time-pro': {
        icon: 'Clock',
        label: 'On-Time Pro',
        emoji: '🔧',
        color: 'bg-gradient-to-r from-lime-500 to-green-500 text-white',
        borderColor: 'border-lime-400',
        description: '95%+ on-time arrival & completion rate'
      },
      'customer-favorite': {
        icon: 'Heart',
        label: 'Customer Favorite',
        emoji: '⭐',
        color: 'bg-gradient-to-r from-pink-500 to-rose-500 text-white',
        borderColor: 'border-pink-400',
        description: 'High save-to-favorites & click-through rate'
      },
      'quote-accuracy': {
        icon: 'Target',
        label: 'Quote Accuracy',
        emoji: '📝',
        color: 'bg-gradient-to-r from-indigo-500 to-blue-500 text-white',
        borderColor: 'border-indigo-400',
        description: '<10% deviation between quote & final cost'
      }
    };

    return configs?.[badgeType] || {};
  };

  const isEligible = calculateBadgeEligibility(vendor, badgeType);
  const config = getBadgeConfig(badgeType);

  if (!isEligible || !config?.label) {
    return null;
  }

  const sizeClasses = {
    xs: 'px-1.5 py-0.5 text-xs',
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base'
  };

  const iconSizes = {
    xs: 10,
    sm: 12,
    md: 14,
    lg: 16
  };

  return (
    <div 
      className={`inline-flex items-center space-x-1 rounded-full font-medium ${config?.color} ${sizeClasses?.[size]} border ${config?.borderColor} shadow-sm`}
      title={config?.description}
    >
      <span className="text-current opacity-90">{config?.emoji}</span>
      <Icon name={config?.icon} size={iconSizes?.[size]} className="fill-current" />
      <span>{config?.label}</span>
    </div>
  );
};

export const VendorBadgeCollection = ({ vendor, maxBadges = 3, size = 'sm' }) => {
  const allBadgeTypes = [
    'featured-vendor', // Prioritize paid badges
    'mibuy-top-rated',
    'qualified-pro',
    'mibuy-preferred-vendor',
    'good-communication',
    'consistent-performer',
    'on-time-pro',
    'customer-favorite',
    'quote-accuracy',
    'identity-verified',
    'licensed-insured',
    'manufacturer-certified'
  ];

  const eligibleBadges = allBadgeTypes?.filter(badgeType => {
    const config = {
      rating: vendor?.rating || 0,
      completedJobs: vendor?.completedJobs || vendor?.completedProjects || 0,
      complaintRate: vendor?.complaintRate || 0,
      platformActivityDays: vendor?.platformActivityDays || 30,
      medianResponseTime: vendor?.medianResponseTime || 2,
      leadResponseRate: vendor?.leadResponseRate || 95,
      maxResponseGap: vendor?.maxResponseGap || 12,
      completedMiBuyProjects: vendor?.completedMiBuyProjects || vendor?.completedProjects || 0,
      hasIdentityVerification: vendor?.hasIdentityVerification || true,
      hasBusinessLicense: vendor?.hasBusinessLicense || vendor?.certifications?.includes('Licensed') || false,
      noRecentDisputes: vendor?.noRecentDisputes !== false,
      quoteAcceptanceRate: vendor?.quoteAcceptanceRate || 60,
      usesToolsConsistently: vendor?.usesToolsConsistently !== false,
      quotesDelivered: vendor?.quotesDelivered || vendor?.completedProjects || 0,
      cancellationRate: vendor?.cancellationRate || 1,
      profileCompletion: vendor?.profileCompletion || 95,
      quotesPerMonth: vendor?.quotesPerMonth || 15,
      noTosViolations: vendor?.noTosViolations !== false,
      engagementScore: vendor?.engagementScore || 85,
      hasGovernmentId: vendor?.hasGovernmentId !== false,
      hasVerifiedPhone: vendor?.hasVerifiedPhone !== false,
      hasVerifiedEmail: vendor?.hasVerifiedEmail !== false,
      hasValidatedEin: vendor?.hasValidatedEin !== false,
      hasConfirmedAddress: vendor?.hasConfirmedAddress !== false,
      hasInsuranceCertificate: vendor?.hasInsuranceCertificate || vendor?.certifications?.includes('Insured') || false,
      manuallyValidated: vendor?.manuallyValidated !== false,
      hasManufacturerCertification: vendor?.hasManufacturerCertification || vendor?.certifications?.includes('Certified Dealer') || vendor?.certifications?.includes('Factory Authorized') || false,
      isPaidFeatured: vendor?.isPaidFeatured || vendor?.isSponsored || false,
      onTimeRate: vendor?.onTimeRate || 96,
      favoritesSaveRate: vendor?.favoritesSaveRate || 80,
      quoteAccuracyRate: vendor?.quoteAccuracyRate || 92
    };

    // Use the outer scope calculateBadgeEligibility function
    const VendorBadgeInstance = { 
      calculateBadgeEligibility: (vendor, badgeType) => calculateBadgeEligibility(vendor, badgeType)
    };
    return VendorBadgeInstance?.calculateBadgeEligibility(config, badgeType);
  });

  const displayBadges = eligibleBadges?.slice(0, maxBadges);

  if (displayBadges?.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {displayBadges?.map((badgeType) => (
        <VendorBadge
          key={badgeType}
          vendor={vendor}
          badgeType={badgeType}
          size={size}
        />
      ))}
      {eligibleBadges?.length > maxBadges && (
        <span className={`px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium ${size === 'xs' ? 'px-1.5 py-0.5' : ''}`}>
          +{eligibleBadges?.length - maxBadges} more
        </span>
      )}
    </div>
  );
};

// Helper function for badge eligibility calculation
const calculateBadgeEligibility = (vendor, badgeType) => {
  const {
    rating = 0,
    completedJobs = 0,
    complaintRate = 0,
    platformActivityDays = 0,
    medianResponseTime = 24,
    leadResponseRate = 0,
    maxResponseGap = 48,
    completedMiBuyProjects = 0,
    hasIdentityVerification = false,
    hasBusinessLicense = false,
    noRecentDisputes = true,
    quoteAcceptanceRate = 0,
    usesToolsConsistently = false,
    quotesDelivered = 0,
    cancellationRate = 0,
    profileCompletion = 0,
    quotesPerMonth = 0,
    noTosViolations = true,
    engagementScore = 0,
    hasGovernmentId = false,
    hasVerifiedPhone = false,
    hasVerifiedEmail = false,
    hasValidatedEin = false,
    hasConfirmedAddress = false,
    hasInsuranceCertificate = false,
    manuallyValidated = false,
    hasManufacturerCertification = false,
    isPaidFeatured = false,
    onTimeRate = 0,
    favoritesSaveRate = 0,
    quoteAccuracyRate = 0
  } = vendor;

  switch (badgeType) {
    case 'mibuy-top-rated':
      return rating >= 4.5 && completedJobs >= 10 && complaintRate < 5 && platformActivityDays >= 30;
    
    case 'good-communication':
      return medianResponseTime < 4 && leadResponseRate >= 90 && maxResponseGap <= 24;
    
    case 'qualified-pro':
      return rating >= 4.0 && completedMiBuyProjects >= 5 && hasIdentityVerification && hasBusinessLicense && noRecentDisputes;
    
    case 'mibuy-preferred-vendor':
      return quoteAcceptanceRate >= 50 && usesToolsConsistently && quotesDelivered >= 10 && cancellationRate < 3;
    
    case 'consistent-performer':
      return profileCompletion >= 90 && quotesPerMonth >= 10 && noTosViolations && engagementScore >= 80;
    
    case 'identity-verified':
      return hasGovernmentId && hasVerifiedPhone && hasVerifiedEmail && hasValidatedEin && hasConfirmedAddress;
    
    case 'licensed-insured':
      return hasBusinessLicense && hasInsuranceCertificate && manuallyValidated;
    
    case 'manufacturer-certified':
      return hasManufacturerCertification;
    
    case 'featured-vendor':
      return isPaidFeatured;
    
    case 'on-time-pro':
      return onTimeRate >= 95;
    
    case 'customer-favorite':
      return favoritesSaveRate >= 75;
    
    case 'quote-accuracy':
      return quoteAccuracyRate >= 90;
    
    default:
      return false;
  }
};

export default VendorBadge;